import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper, handleResponse } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import {
  generateAccessTokenQuery,
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { createSingleJoinedSpark } from 'helpers/spark';
import { useCurrentUser } from 'hooks/common';
import type { SingleSparkResponse } from 'types/Spark/Spark';
import React from 'react';
import type { ActionPrompt } from 'types/common/ErrorResponse';

export function useSparksByID(
  sparkID: string | undefined | null,
  accessToken?: string | null
) {
  const { currentUserID } = useCurrentUser();
  const [permissionDetailsErrors, setPermissionDetailsErrors] = React.useState<
    ActionPrompt | undefined
  >(undefined);

  const getSparkByID = async (
    sparkID: string | undefined | null,
    accessToken: string | null = null,
    userId: string | undefined
  ) => {
    //TODO: do not let queryParams decode when use GET as fetch method
    //remove double invoking of encodeURIComponent
    const queryArray = [
      ...(accessToken && !userId
        ? [
            generateAccessTokenQuery(
              encodeURIComponent(encodeURIComponent(accessToken))
            ),
          ]
        : [
            generateIncludeQuery([
              'sparkCategories',
              'marketplaceAccount',
              'sparkImages',
              'sparkRatings',
              'sparkFavourites',
            ]),
          ]),
    ];

    const url = generateURL(SPARK_RESOURCE_TYPES.SPARKS, queryArray, sparkID);

    const response = await fetchWrapper<SingleSparkResponse, true>({
      url: url,
      method: 'GET',
      rawResponse: true,
    });

    const handleResCallback = (res: any) => {
      const error = res?.errors?.[0];
      const permissionDetailsError = error?.meta
        ?.permissionDetails as ActionPrompt;
      if (permissionDetailsError && response.status === 403) {
        setPermissionDetailsErrors(permissionDetailsError);
      }
    };

    return handleResponse<SingleSparkResponse>(response, handleResCallback);
  };

  const response = useQuery(
    ['sparks', sparkID],
    () => getSparkByID(sparkID, accessToken, currentUserID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Spark: ${error.message}`);
        }
      },
      onSuccess: (data) => {
        const actionPrompt = data.data.meta.user.usageRights?.actionPrompt;
        if (actionPrompt) {
          setPermissionDetailsErrors(actionPrompt);
        }
      },

      retry: 1,
      enabled: Boolean(sparkID),
      ...CACHE_AND_STALE_TIME,
    }
  );
  const spark = response.data?.data;
  const isLoading = response.isLoading;
  const isFetch = response.isFetching;
  return {
    ...response,
    spark,
    joinedSpark: createSingleJoinedSpark(response.data),
    isLoading,
    isFetch,
    permissionDetailsErrors,
  };
}
